// src/components/common/actionBeat/resolveConfirmBeat.js
//
// The single gate BOTH confirm shells (ConfirmDialog and ConfirmDialogInline) call to
// decide which action-beat visual a given `actionBeat` config plays. KAN-1078 (founder,
// 2026-10-28): EVERY write plays the ONE Fill beat -- the Cancel folds, the primary grows
// to the full row, working -> done in the same box, then the overlay closes. The old RING
// (hero card) and the legacy sweep bar are gone; a caller that still declares
// `kind: 'ring'` (with a subject/status) simply plays Fill.
//
// Pure -- no JSX, no hooks -- so both shells can call it identically and a parity test
// can assert they agree without rendering either one twice.

import { beatTiming } from '../../../ui/actionBeatMotion';

const FILL_TONES = ['delete', 'security'];

/**
 * @param {object|null|undefined} actionBeat the `actionBeat` prop/config a confirm caller passes.
 * @param {{danger?: boolean}} [opts] `danger`: the dialog's confirm is destructive (tone 'danger');
 *   a beat with no explicit fill tone then finishes neutral, not green.
 * @returns {{ kind: 'fill'|null, timing: object, fill?: object }}
 *   `kind: null` -- no actionBeat at all; the beat machinery stays dormant.
 *   `kind: 'fill'` -- the tapped button itself fills; `fill.tone` is what ActionBeatFill needs
 *     (workingLabel/successLabel/confirmLabel come straight off the dialog's props/actionBeat).
 */
export function resolveConfirmBeat(actionBeat, { danger = false } = {}) {
  if (!actionBeat) return { kind: null, timing: {} };
  if (actionBeat.kind === 'fill') {
    return {
      kind: 'fill',
      timing: beatTiming('fill'),
      fill: { tone: FILL_TONES.includes(actionBeat.tone) ? actionBeat.tone : 'save' },
    };
  }
  // Any other shape (no kind, a legacy 'ring'): Fill with the old bar's timing (`{}` = no
  // min-working floor, the default 650ms hold); a destructive confirm finishes NEUTRAL
  // ('delete' tone), never green.
  return { kind: 'fill', timing: {}, fill: { tone: danger ? 'delete' : 'save' } };
}
